
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AuthState } from '@/hooks/auth/types';
import { performUserSessionCleanup } from '@/utils/auth-cleanup';
import { logger } from '@/utils/logger';


/**
 * Centralized Auth Context - Single source of truth for authentication state
 * This eliminates the multiple auth subscriptions and state checks that were causing duplicate events
 */
const AuthContext = createContext<AuthState | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    loading: true,
    isLoading: true
  });
  
  // Enhanced refs for better state tracking and performance
  const previousUserIdRef = useRef<string | null>(null);
  const previousAuthenticatedRef = useRef<boolean>(false);
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef<boolean>(false);

  // Centralized state update function
  const updateAuthState = (newState: Partial<AuthState>) => {
    setAuthState(prevState => ({ ...prevState, ...newState }));
  };

  useEffect(() => {
    let isMounted = true;
    
    logger.auth('Initializing centralized auth state management');
    
    const checkAuthStatus = async () => {
      try {
        // Use Supabase's getSession method to check authentication status
        // This is the authoritative source of truth for authentication
        const { data, error } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        if (error) {
          logger.error('Error getting session:', error);
          updateAuthState({
            user: null,
            isAuthenticated: false,
            loading: false,
            isLoading: false
          });
          return;
        }
        
        const newUser = data.session?.user || null;
        const newIsAuthenticated = !!data.session;
        
        logger.auth('Initial auth check:', {
          userId: newUser?.id,
          isAuthenticated: newIsAuthenticated,
          hasSession: !!data.session
        });
        
        // Update state on initial load
        updateAuthState({
          user: newUser,
          isAuthenticated: newIsAuthenticated,
          loading: false,
          isLoading: false
        });
        
        // Update refs for next comparison
        previousUserIdRef.current = newUser?.id || null;
        previousAuthenticatedRef.current = newIsAuthenticated;
        isInitializedRef.current = true;
      } catch (error) {
        logger.error('Error checking auth status:', error);
        if (!isMounted) return;
        
        updateAuthState({
          user: null,
          isAuthenticated: false,
          loading: false,
          isLoading: false
        });
      }
    };

    checkAuthStatus();

    // Enhanced auth state change listener - SINGLE SUBSCRIPTION FOR ENTIRE APP
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        
        // Skip duplicate INITIAL_SESSION events after initialization
        if (event === 'INITIAL_SESSION' && isInitializedRef.current) {
          return;
        }
        
        logger.auth('Auth state change:', event, 'User ID:', session?.user?.id);
        
        const newUser = session?.user || null;
        const newIsAuthenticated = !!session;
        const previousUserId = previousUserIdRef.current;
        const previousAuthenticated = previousAuthenticatedRef.current;
        
        // Enhanced cleanup handling
        if (cleanupTimeoutRef.current) {
          clearTimeout(cleanupTimeoutRef.current);
        }
        
        // If user signed out or session ended, perform cleanup
        if (event === 'SIGNED_OUT' || (!session && previousAuthenticated)) {
          logger.auth('User signed out, performing cleanup');
          cleanupTimeoutRef.current = setTimeout(() => {
            performUserSessionCleanup(queryClient);
          }, 100);
        }
        
        // If user changed (different user ID), perform cleanup
        if (newUser && previousUserId && newUser.id !== previousUserId) {
          logger.auth('User changed, performing cleanup');
          cleanupTimeoutRef.current = setTimeout(() => {
            performUserSessionCleanup(queryClient);
          }, 100);
        }
        
        // Update auth state immediately
        updateAuthState({
          user: newUser,
          isAuthenticated: newIsAuthenticated,
          loading: false,
          isLoading: false
        });
        
        // Update refs for performance tracking
        previousUserIdRef.current = newUser?.id || null;
        previousAuthenticatedRef.current = newIsAuthenticated;
      }
    );

    // Cleanup when component unmounts
    return () => {
      logger.auth('Cleaning up centralized auth state management');
      isMounted = false;
      subscription.unsubscribe();
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
    };
  }, []); // Empty dependency array to prevent infinite loops

  return (
    <AuthContext.Provider value={authState}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthState => {
  const context = useContext(AuthContext);
  
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
